import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { QueryKey, useQueryClient } from 'react-query';
import { FiAlertTriangle, FiCheck } from 'react-icons/fi';
import { HiOutlineDownload } from 'react-icons/hi';

import Button from '../../components/Button';
import DefaultModal from '../../components/modals/DefaultModal';
import ToastNotification from '../../components/ToastNotification';
import { useOperatorToken } from '../../hooks/useOperatorToken';
import {
  fetchItpFileDownload,
  markItpReady,
  removeItpFile,
  uploadItpFile,
} from './api';
import { SignInAgainNotice } from './BookTicketPanels';
import {
  canEditItp,
  formatDateTime,
  formatFileSize,
  getItpRefusalCode,
  hasPdfSignature,
  isApprovalPendingStatus,
  isItpPendingStatus,
  isItpPrinterSending,
  isOperatorUnverified,
  itpErrorMessage,
  itpErrorNeedsTicketRefetch,
  printFileClientError,
  shortOrderId,
} from './helpers';
import { formatMessage, printShopStrings } from './strings';
import { TicketDetail, TicketItp, TicketItpFile, TicketItpStatus } from './types';

/**
 * The Instant Trust Package card of a book order (itp-mvp, option A without
 * the pieces checklist): the ITP PDFs an operator uploads, and "Mark ITP
 * ready", which moves the order to Paid and releases the printer.
 *
 * Every change waits for DS and writes the ticket DS answers into the ticket
 * query; the queue and the counts are refetched, since the order may have
 * changed lanes. The ticket itself is refetched only when a refusal leaves it
 * in doubt. Read-only once the ITP is ready or the order moved on.
 */

const STRINGS = printShopStrings.detail.itp;

/** Same darker teal as the approval card's actions (contrast above 5:1). */
const FILLED_ACTION_CLASS =
  'inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-[#1f7383] bg-[#1f7383] px-4 text-sm font-bold text-white transition-colors hover:border-[#165a67] hover:bg-[#165a67] focus:outline-none focus:ring-2 focus:ring-[#1f7383] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

const LINK_BUTTON_CLASS =
  'text-sm font-semibold text-primary-600 hover:underline disabled:opacity-60 disabled:no-underline';

const CHIP_CLASS: Record<TicketItpStatus, string> = {
  pending: 'bg-orange-100 text-orange-700',
  ready: 'bg-success-50 text-success-700',
};

/** Progress from which the upload reads "Saving…": DS is storing the file. */
const SAVING_FROM_PERCENT = 95;

/** A per-file refusal: the next picked file may still go. */
const isPerFileRefusal = (error: unknown): boolean =>
  getItpRefusalCode(error)?.startsWith('PRINT_FILE_') ?? false;

interface UploadProgress {
  name: string;
  current: number;
  total: number;
  percent: number;
}

const uploadLabel = (upload: UploadProgress): string =>
  formatMessage(
    upload.percent >= SAVING_FROM_PERCENT ? STRINGS.saving : STRINGS.uploading,
    {
      name: upload.name,
      current: String(upload.current),
      total: String(upload.total),
      percent: String(upload.percent),
    },
  );

/** "{name}: {reason}", so a toast always says which file it is about. */
const refuseFile = (name: string, reason: string) =>
  ToastNotification({
    type: 'failure',
    message: formatMessage(STRINGS.fileRefused, { name, reason }),
  });

/** Tracks whether the component is still on the page across awaits. */
const useMountedRef = () => {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
};

const ItpFileRow = ({
  orderId,
  file,
  canRemove,
  isRemoving,
  disabled,
  onRemove,
  onFileGone,
}: {
  orderId: string;
  file: TicketItpFile;
  canRemove: boolean;
  isRemoving: boolean;
  disabled: boolean;
  onRemove: () => void;
  /** DS no longer has the file (404): the ticket is refetched. */
  onFileGone: () => void;
}) => {
  const mounted = useMountedRef();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  /**
   * A fresh signed URL PER CLICK, never cached, like the print files. The
   * tab is opened inside the click and pointed at the URL once it arrives: a
   * window.open after the await runs outside the user gesture, and the popup
   * blocker eats it. On failure the placeholder tab is closed.
   */
  const onDownload = async () => {
    if (isDownloading) {
      return;
    }
    setIsDownloading(true);
    setDownloadError(null);
    const tab = window.open('', '_blank');
    if (tab) {
      // The signed URL's page must not reach back into ABO.
      tab.opener = null;
    }
    try {
      const { url } = await fetchItpFileDownload(orderId, file.id);
      if (tab) {
        tab.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (error) {
      tab?.close();
      const gone = getItpRefusalCode(error) === 'ITP_FILE_NOT_FOUND';
      if (mounted.current) {
        setDownloadError(
          gone ? STRINGS.refusals.ITP_FILE_NOT_FOUND : STRINGS.downloadFailed,
        );
      }
      if (gone) {
        onFileGone();
      }
    } finally {
      if (mounted.current) {
        setIsDownloading(false);
      }
    }
  };

  return (
    <li
      data-testid={`itp-file-${file.id}`}
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-neutral-200 px-4 py-3"
    >
      <span className="flex min-w-0 flex-col">
        <span className="break-all text-sm font-semibold text-neutral-800">
          {file.fileName}
        </span>
        <span className="text-xs text-neutral-500">
          {formatMessage(STRINGS.fileMeta, {
            email: file.uploadedBy || '—',
            date: formatDateTime(file.uploadedAt),
            size: formatFileSize(file.size),
          })}
        </span>
      </span>
      <span className="flex items-center gap-4">
        <button
          type="button"
          data-testid={`itp-file-download-${file.id}`}
          disabled={isDownloading}
          onClick={() => void onDownload()}
          className={`inline-flex items-center gap-1.5 ${LINK_BUTTON_CLASS}`}
        >
          <HiOutlineDownload aria-hidden />
          {isDownloading ? STRINGS.downloading : STRINGS.download}
        </button>
        {canRemove && (
          <button
            type="button"
            data-testid={`itp-file-remove-${file.id}`}
            disabled={disabled}
            onClick={onRemove}
            className="text-sm font-semibold text-neutral-500 hover:underline disabled:opacity-60 disabled:no-underline"
          >
            {isRemoving ? STRINGS.removing : STRINGS.remove}
          </button>
        )}
        {downloadError && (
          <span
            data-testid={`itp-file-download-error-${file.id}`}
            className="text-sm text-red-600"
          >
            {downloadError}
          </span>
        )}
      </span>
    </li>
  );
};

/**
 * Where the printer release stands once the ITP is ready (`itp.printer`).
 * Nothing on a DS that does not send it, nor when it does not apply.
 */
const ItpPrinterLine = ({ itp, now }: { itp: TicketItp; now: number }) => {
  const { printer } = itp;
  if (!printer || printer.status === 'not_applicable') {
    return null;
  }
  const strings = STRINGS.printer;
  let value: React.ReactNode;
  switch (printer.status) {
    case 'held':
      value = strings.held;
      break;
    case 'pending':
      value = isItpPrinterSending(itp, now) ? strings.sending : strings.notSent;
      break;
    case 'sent':
      value = (
        <span className="inline-flex items-center gap-1.5 text-success-700">
          <FiCheck aria-hidden className="h-3.5 w-3.5 flex-none" />
          {printer.at
            ? formatMessage(strings.sentAt, { at: formatDateTime(printer.at) })
            : strings.sent}
        </span>
      );
      break;
    case 'failed':
      value = (
        <span className="inline-flex items-center gap-1.5 text-error-700">
          <FiAlertTriangle aria-hidden className="h-3.5 w-3.5 flex-none" />
          {strings.failed}
        </span>
      );
      break;
    default:
      value = printer.status;
  }
  return (
    <p
      data-testid="itp-printer"
      data-status={printer.status}
      className="flex flex-wrap gap-x-2 text-sm text-neutral-600"
    >
      <span>{strings.label}</span>
      <span className="font-semibold text-neutral-800">{value}</span>
    </p>
  );
};

export const BookItpPanel = ({
  ticket,
  queryKey,
}: {
  ticket: TicketDetail;
  /** The ticket query: each change writes DS's answer into it. */
  queryKey: QueryKey;
}) => {
  const queryClient = useQueryClient();
  const { getOperatorToken, signInAgain } = useOperatorToken();
  const mounted = useMountedRef();
  const fileInput = useRef<HTMLInputElement>(null);
  // Aborts the upload in flight when the card leaves the page.
  const uploadAbort = useRef<AbortController | null>(null);
  const [upload, setUpload] = useState<UploadProgress | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  // DS could not verify the operator's sign-in on the last change.
  const [needsSignIn, setNeedsSignIn] = useState(false);

  useEffect(() => () => uploadAbort.current?.abort(), []);

  const { itp } = ticket;
  if (!itp) {
    return null;
  }

  const editable = canEditItp({ status: ticket.status, itp });
  const isReady = itp.status === 'ready';
  // Marked ready, but the move to Paid did not finish (DS failed half-way).
  const needsFinish = isReady && isItpPendingStatus(ticket.status);
  const files = itp.files ?? [];
  const busy = upload !== null || removingId !== null || isMarking;
  // DS accepts the ITP while the member's approval is still open; the order
  // then waits for the approval, not for the ITP.
  const approvalStillOpen = isApprovalPendingStatus(ticket.status);
  const orderNumber = ticket.afyOrderNumber ?? shortOrderId(ticket.orderId);

  /** The order may have changed lanes: the queue and its counts. */
  const refreshLists = () => {
    queryClient.invalidateQueries('print-shop-tickets');
    queryClient.invalidateQueries('print-shop-ticket-counts');
  };

  /** DS answered the ticket: show it as it is now. */
  const applyTicket = (updated: TicketDetail) => {
    queryClient.setQueryData(queryKey, updated);
  };

  const refetchTicket = () => {
    queryClient.invalidateQueries(queryKey);
  };

  /** A refusal; the ticket is refetched only when it leaves it in doubt. */
  const fail = (error: unknown, fallback: string, fileName?: string) => {
    if (itpErrorNeedsTicketRefetch(error)) {
      refetchTicket();
      refreshLists();
    }
    if (isOperatorUnverified(error)) {
      setNeedsSignIn(true);
      return;
    }
    const reason = itpErrorMessage(error, fallback);
    if (fileName) {
      refuseFile(fileName, reason);
      return;
    }
    ToastNotification({ type: 'failure', message: reason });
  };

  const onFilesPicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    // Cleared so picking the same files again still fires a change.
    event.target.value = '';
    // Checked in the browser before anything is sent: type, size, then the
    // `%PDF-` signature DS checks too.
    const accepted: File[] = [];
    for (const file of picked) {
      const reason = printFileClientError(file);
      if (reason) {
        refuseFile(file.name, reason);
      } else if (!(await hasPdfSignature(file))) {
        refuseFile(file.name, STRINGS.refusals.PRINT_FILE_NOT_PDF);
      } else {
        accepted.push(file);
      }
    }
    if (accepted.length === 0 || !mounted.current) {
      return;
    }
    const controller = new AbortController();
    uploadAbort.current = controller;
    let changed = false;
    try {
      // One file per request, in turn, so each has its own progress and its
      // own answer.
      for (const [index, file] of accepted.entries()) {
        if (!mounted.current) {
          return;
        }
        setUpload({
          name: file.name,
          current: index + 1,
          total: accepted.length,
          percent: 0,
        });
        try {
          // Read (after a refresh) for this one request only.
          const token = await getOperatorToken();
          const updated = await uploadItpFile(
            ticket.orderId,
            file,
            { token },
            (percent) =>
              setUpload((current) => (current ? { ...current, percent } : current)),
            controller.signal,
          );
          if (!mounted.current) {
            return;
          }
          applyTicket(updated);
          changed = true;
          setNeedsSignIn(false);
          ToastNotification({
            type: 'success',
            message: formatMessage(STRINGS.fileAdded, { name: file.name }),
          });
        } catch (error) {
          if (!mounted.current || axios.isCancel(error)) {
            return;
          }
          fail(error, STRINGS.uploadFailed, file.name);
          // A refusal of this file lets the next one go; anything else (sign-in,
          // order closed, no bucket, network) would refuse them all.
          if (!isPerFileRefusal(error)) {
            const notSent = accepted.slice(index + 1).map(({ name }) => name);
            if (notSent.length > 0) {
              ToastNotification({
                type: 'failure',
                message: formatMessage(STRINGS.notSent, {
                  names: notSent.join(', '),
                }),
              });
            }
            break;
          }
        }
      }
    } finally {
      if (uploadAbort.current === controller) {
        uploadAbort.current = null;
      }
      if (mounted.current) {
        setUpload(null);
        if (changed) {
          refreshLists();
        }
      }
    }
  };

  const onRemove = async (file: TicketItpFile) => {
    setRemovingId(file.id);
    try {
      const token = await getOperatorToken();
      const updated = await removeItpFile(ticket.orderId, file.id, { token });
      if (!mounted.current) {
        return;
      }
      applyTicket(updated);
      refreshLists();
      setNeedsSignIn(false);
      ToastNotification({
        type: 'success',
        message: formatMessage(STRINGS.fileRemoved, { name: file.fileName }),
      });
    } catch (error) {
      if (mounted.current) {
        fail(error, formatMessage(STRINGS.removeFailed, { name: file.fileName }));
      }
    } finally {
      if (mounted.current) {
        setRemovingId(null);
      }
    }
  };

  /**
   * Mark ITP ready, and its retry "Finish — move to Paid": DS is safe to
   * call again and finishes a move that stopped half-way.
   */
  const onMarkReady = async () => {
    if (isMarking) {
      return;
    }
    setIsMarking(true);
    try {
      const token = await getOperatorToken();
      const updated = await markItpReady(ticket.orderId, { token });
      if (!mounted.current) {
        return;
      }
      applyTicket(updated);
      refreshLists();
      setNeedsSignIn(false);
      ToastNotification({
        type: 'success',
        message: formatMessage(STRINGS.markedReady, { orderNumber }),
      });
    } catch (error) {
      if (mounted.current) {
        fail(error, STRINGS.markReadyFailed);
      }
    } finally {
      if (mounted.current) {
        setIsMarking(false);
        setIsConfirming(false);
      }
    }
  };

  const showActionCard = editable || isReady;

  return (
    <section
      data-testid="itp-panel"
      data-status={itp.status}
      className="my-6 flex flex-col gap-4 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
    >
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xs font-bold uppercase tracking-wider text-neutral-500">
            {STRINGS.sectionTitle}
          </h2>
          <span
            data-testid="itp-state"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${CHIP_CLASS[itp.status] ?? CHIP_CLASS.pending}`}
          >
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
            {STRINGS.states[itp.status] ?? itp.status}
          </span>
        </div>
        <p data-testid="itp-included" className="text-sm text-neutral-600">
          {formatMessage(STRINGS.included, { sets: STRINGS.setsCount(itp.sets) })}
        </p>
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-5">
        {showActionCard && (
          <div
            data-testid="itp-action-card"
            className="flex flex-col gap-3 rounded-[10px] bg-neutral-50 p-5 lg:col-span-2"
          >
            {editable ? (
              <>
                <button
                  type="button"
                  data-testid="itp-mark-ready"
                  disabled={files.length === 0 || busy}
                  onClick={() => setIsConfirming(true)}
                  className={FILLED_ACTION_CLASS}
                >
                  <FiCheck aria-hidden className="h-4 w-4 flex-none" />
                  {STRINGS.markReady}
                </button>
                <p
                  data-testid="itp-mark-ready-hint"
                  className="text-xs text-neutral-500"
                >
                  {files.length === 0
                    ? STRINGS.markReadyNeedsFile
                    : approvalStillOpen
                      ? STRINGS.markReadyHintApproval
                      : STRINGS.markReadyHint}
                </p>
              </>
            ) : (
              <>
                <p
                  data-testid="itp-ready-by"
                  className="flex items-center gap-1.5 text-sm font-semibold text-success-700"
                >
                  <FiCheck aria-hidden className="h-4 w-4 flex-none" />
                  {formatMessage(STRINGS.readyBy, {
                    email: itp.readyBy?.id ?? '—',
                    date: formatDateTime(itp.readyAt ?? undefined),
                  })}
                </p>
                {needsFinish && (
                  <>
                    <button
                      type="button"
                      data-testid="itp-finish"
                      disabled={busy}
                      onClick={() => void onMarkReady()}
                      className={FILLED_ACTION_CLASS}
                    >
                      {STRINGS.finishButton}
                    </button>
                    <p
                      data-testid="itp-finish-hint"
                      className="text-xs text-neutral-500"
                    >
                      {STRINGS.finishHint}
                    </p>
                  </>
                )}
                <ItpPrinterLine itp={itp} now={Date.now()} />
              </>
            )}
          </div>
        )}

        <div
          className={`flex flex-col gap-2 ${
            showActionCard ? 'lg:col-span-3' : 'lg:col-span-5'
          }`}
        >
          <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-400">
            {STRINGS.filesTitle}
          </h3>
          {files.length === 0 ? (
            <p data-testid="itp-no-files" className="text-sm text-neutral-500">
              {STRINGS.noFiles}
            </p>
          ) : (
            <ul data-testid="itp-files" className="flex flex-col gap-2">
              {files.map((file) => (
                <ItpFileRow
                  key={file.id}
                  orderId={ticket.orderId}
                  file={file}
                  canRemove={editable}
                  isRemoving={removingId === file.id}
                  disabled={busy}
                  onRemove={() => void onRemove(file)}
                  onFileGone={refetchTicket}
                />
              ))}
            </ul>
          )}
          {editable && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-neutral-300 px-4 py-3">
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf"
                multiple
                hidden
                data-testid="itp-file-input"
                onChange={(event) => void onFilesPicked(event)}
              />
              <button
                type="button"
                data-testid="itp-add-files"
                disabled={busy}
                onClick={() => fileInput.current?.click()}
                className={LINK_BUTTON_CLASS}
              >
                {upload ? uploadLabel(upload) : STRINGS.addFiles}
              </button>
              <span className="text-xs text-neutral-500">{STRINGS.addFilesHint}</span>
            </div>
          )}
        </div>
      </div>

      {needsSignIn && <SignInAgainNotice onSignIn={signInAgain} />}

      <DefaultModal
        title={STRINGS.confirmTitle}
        showModal={isConfirming}
        setShowModal={(show) => {
          if (!show && !isMarking) setIsConfirming(false);
        }}
        size="md"
      >
        {isConfirming && (
          <div data-testid="itp-confirm" className="flex flex-col gap-4">
            <p className="text-sm text-neutral-800">
              {formatMessage(
                approvalStillOpen
                  ? STRINGS.confirmMessageApproval
                  : STRINGS.confirmMessage,
                { orderNumber },
              )}
            </p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 text-sm">
              <dt className="text-neutral-500">{STRINGS.confirmFiles}</dt>
              <dd data-testid="itp-confirm-files" className="text-neutral-800">
                {STRINGS.confirmFilesCount(files.length)}
              </dd>
            </dl>
            <div className="flex justify-end gap-2">
              <Button
                variant="OUTLINED"
                disabled={isMarking}
                onClick={() => setIsConfirming(false)}
              >
                {STRINGS.confirmCancel}
              </Button>
              <Button
                variant="PRIMARY"
                data-testid="itp-confirm-button"
                isProcessing={isMarking}
                disabled={isMarking}
                onClick={() => void onMarkReady()}
              >
                {STRINGS.confirmButton}
              </Button>
            </div>
          </div>
        )}
      </DefaultModal>
    </section>
  );
};
